import { AppStorageKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import LocalStorage from '@vantiva-sa/homesight_humaui/Utilities/LocalStorage';
import { getUserConfig } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

const normalizeLocationToken = (value) =>
  typeof value === 'string' ? value.replace(/\s+/g, '').toLowerCase() : '';

const convertToValidJSON = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  let fixedValue = value.replace(/'/g, '"');
  fixedValue = fixedValue.replace(/(\b\w+\b):/g, '"$1":');
  fixedValue = fixedValue.replace(/,\s*}/g, '}').replace(/,\s*]/g, ']');
  return fixedValue;
};

const parseLocationsConfig = (rawValue) => {
  if (Array.isArray(rawValue)) {
    return rawValue;
  }

  if (typeof rawValue !== 'string' || rawValue.trim() === '') {
    return [];
  }

  try {
    return JSON.parse(convertToValidJSON(rawValue));
  } catch (error) {
    return [];
  }
};

const useLocationTranslation = (useruuid) => {
  const currentUserConfig = useSelector(getUserConfig(useruuid));

  const translateLocation = useCallback(
    (locationValue, deviceType) => {
      if (typeof locationValue !== 'string' || locationValue.trim() === '') {
        return locationValue;
      }

      if (typeof deviceType !== 'string' || deviceType.trim() === '') {
        return locationValue;
      }

      const selectedLanguage = LocalStorage.get(AppStorageKeys.language) || 'en';
      const languageCode =
        typeof selectedLanguage === 'string' && selectedLanguage.trim()
          ? selectedLanguage.toLowerCase().split('-')[0]
          : 'en';
      const locationNamesKey = `location_names_${languageCode}`;

      const sensorLocationSetup =
        currentUserConfig?.smart_home_sensors_location_setup ||
        currentUserConfig?.homesight_fti?.smart_home_sensors_location_setup;
      const sensorConfig = sensorLocationSetup?.[deviceType];
      const translatedLocations = parseLocationsConfig(
        sensorConfig?.[locationNamesKey] || sensorConfig?.location_names_en
      );

      if (!Array.isArray(translatedLocations) || translatedLocations.length === 0) {
        return locationValue;
      }

      const [rawPrimaryPart, rawSecondaryPart] = locationValue.split('-');
      const primaryPart = rawPrimaryPart?.trim();
      const secondaryPart = rawSecondaryPart?.trim();

      const normalizedPrimaryPart = normalizeLocationToken(primaryPart);
      const locationEntry = translatedLocations.find(
        (entry) => entry?.locationIndex === normalizedPrimaryPart
      );

      if (!locationEntry) {
        return locationValue;
      }

      const translatedPrimaryPart = locationEntry?.location || primaryPart;

      if (!secondaryPart) {
        return translatedPrimaryPart;
      }

      const normalizedSecondaryPart = normalizeLocationToken(secondaryPart);
      const translatedDevice = locationEntry?.devices?.find(
        (device) => device?.index === normalizedSecondaryPart
      );
      const translatedSecondaryPart = translatedDevice?.name || secondaryPart;

      return `${translatedPrimaryPart} - ${translatedSecondaryPart}`;
    },
    [currentUserConfig]
  );

  return { translateLocation };
};

export default useLocationTranslation;
